import { useEffect, useId, useRef } from "react"
import { AMIR_CALL_URL } from "@/lib/constants/support"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"
import { HugeiconsIcon } from "@hugeicons/react"
import {
  ArrowRight01Icon,
  CodeIcon,
  FlashIcon,
  AnalyticsUpIcon,
} from "@hugeicons/core-free-icons"
import amirProfile from "@/assets/onboarding/amir-profile.jpeg"
import callHero from "@/assets/onboarding/call-hero.jpg"

export const ONBOARDING_CALL_URL = AMIR_CALL_URL

const PROOF = [
  {
    icon: CodeIcon,
    title: "Script in 2 minutes",
    caption: "Paste once. Data starts flowing.",
  },
  {
    icon: FlashIcon,
    title: "First test, no deploy",
    caption: "Element tests apply at runtime.",
  },
  {
    icon: AnalyticsUpIcon,
    title: "Revenue in Stripe",
    caption: "See which variant actually pays.",
  },
] as const

type OnboardingCallModalProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  onBook: () => void
  onDismiss: () => void
}

export function OnboardingCallModal({
  open,
  onOpenChange,
  onBook,
  onDismiss,
}: OnboardingCallModalProps) {
  // Explicit ids so the title/description association is stated in the markup
  // rather than inferred from Radix's auto-wiring.
  const titleId = useId()
  const descriptionId = useId()

  /**
   * The dialog is controlled from the wizard, so there is no `DialogTrigger`
   * for Radix to hand focus back to. Remember whatever was focused when it
   * opened and restore that on close, so keyboard users land where they left.
   */
  const triggerRef = useRef<HTMLElement | null>(null)

  useEffect(() => {
    if (!open) return
    triggerRef.current = document.activeElement as HTMLElement | null
    const img = new Image()
    img.src = callHero
  }, [open])

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn(
          "max-w-md gap-4 overflow-hidden rounded-lg p-0",
          // The shared Close sits over the photo, where the inherited dark
          // glyph disappears. Scoped override, not a change to the primitive.
          "[&>button:last-child]:bg-black/40 [&>button:last-child]:text-white [&>button:last-child]:opacity-90 [&>button:last-child]:hover:bg-black/60 [&>button:last-child]:hover:opacity-100",
        )}
        aria-labelledby={titleId}
        aria-describedby={descriptionId}
        onCloseAutoFocus={(event) => {
          const trigger = triggerRef.current
          if (trigger?.isConnected) {
            event.preventDefault()
            trigger.focus()
          }
        }}
      >
        <div className="relative h-40 overflow-hidden bg-muted">
          <img
            src={callHero}
            alt=""
            className="absolute inset-0 size-full object-cover object-right"
          />
          <img
            src={amirProfile}
            alt="Amir, co-founder of Humblytics"
            className="absolute bottom-3 left-4 size-11 rounded-full object-cover ring-2 ring-background"
          />
        </div>
        <div className="px-6 pb-6 pt-1 space-y-4">
          <DialogHeader>
            <DialogTitle id={titleId} className="text-xl font-medium tracking-tight">
              Book a setup call
            </DialogTitle>
            <DialogDescription id={descriptionId} className="text-sm text-foreground/80">
              A 30-minute call to connect your site and pick the first test worth running.
            </DialogDescription>
          </DialogHeader>
          <ul className="rounded-lg border border-border/40 divide-y divide-border/30">
            {PROOF.map((item) => (
              <li key={item.title} className="flex items-start gap-3 px-3 py-2.5">
                <span className="mt-0.5 flex size-7 shrink-0 items-center justify-center rounded-md border border-border/40 bg-muted/40">
                  <HugeiconsIcon icon={item.icon} size={14} className="text-muted-foreground" />
                </span>
                <div>
                  <p className="text-sm font-medium text-foreground">{item.title}</p>
                  <p className="text-xs text-foreground/70">{item.caption}</p>
                </div>
              </li>
            ))}
          </ul>
          <p className="text-xs text-foreground/70">
            Teams who complete a setup call get tracking live the same day. Amir hosts it.
          </p>
          <Button
            className={cn(
              "h-9 w-full gap-2",
              // The modal portals outside the wizard's scope, so the token
              // carries its fallback. `--primary` measures 4.06:1 against a
              // white label; this fill measures above the AA floor.
              "bg-[var(--onboarding-action,oklch(0.54_0.21_42))] text-white",
              "hover:bg-[var(--onboarding-action-hover,oklch(0.5_0.21_42))]",
            )}
            onClick={() => {
              onBook()
              window.open(ONBOARDING_CALL_URL, "_blank", "noopener,noreferrer")
            }}
          >
            Pick a time
            <HugeiconsIcon icon={ArrowRight01Icon} size={14} />
          </Button>
          <button
            type="button"
            onClick={onDismiss}
            className="flex min-h-6 w-full items-center justify-center rounded-md px-3 py-2 text-center text-xs text-foreground/70 outline-none transition-colors hover:text-foreground focus-visible:ring-[2px] focus-visible:ring-ring/30"
          >
            Maybe later
          </button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
